import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import retry from "async-retry";

import { TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE } from "./constants";

const MAX_RETRY_ATTEMPTS = 3;

interface TimekeepingRetryMetadata {
  shiftId: string;
  workerId: string;
  workplaceId: string;
  action: "clock-in" | "clock-out";
}

function isActionNotFoundError(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.message === TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE
  );
}

export async function retryTimekeepingAction<T>(
  action: () => Promise<T>,
  metadata: TimekeepingRetryMetadata
): Promise<T> {
  const startTime = Date.now();
  let attemptNumber = 0;

  try {
    return await retry(
      async (bail) => {
        attemptNumber += 1;

        try {
          return await action();
        } catch (error: unknown) {
          if (!isActionNotFoundError(error)) {
            bail(error instanceof Error ? error : new Error(String(error)));
            throw error;
          }

          logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_INTEGRATION_RETRY_ATTEMPT, {
            shiftId: metadata.shiftId,
            workerId: metadata.workerId,
            workplaceId: metadata.workplaceId,
            action: metadata.action,
            attemptNumber,
            maxAttempts: MAX_RETRY_ATTEMPTS,
            elapsedTimeMs: Date.now() - startTime,
          });

          throw error;
        }
      },
      {
        retries: MAX_RETRY_ATTEMPTS - 1,
        minTimeout: 2000,
        factor: 1,
      }
    );
  } catch (error: unknown) {
    if (isActionNotFoundError(error)) {
      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_INTEGRATION_RETRY_EXHAUSTED, {
        shiftId: metadata.shiftId,
        workerId: metadata.workerId,
        workplaceId: metadata.workplaceId,
        action: metadata.action,
        totalAttempts: attemptNumber,
        totalElapsedTimeMs: Date.now() - startTime,
      });
    }

    throw error;
  }
}
